import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Ticket, Clock, MapPin, User, Warning } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function EventVerify() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [event, setEvent] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/verify/${id}`);
      setTicket(data);
      try {
        const { data: ev } = await api.get(`/events/${data.event_id}`);
        setEvent(ev);
      } catch { /* pending events return 404 for anonymous */ }
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Ticket not found"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const canCheckIn = user && user !== false && event && (event.organizer_id === user.id || user.role === "admin" || user.role === "manager");

  const doCheckin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/checkin/${id}`);
      if (data.already) toast.info("Already checked in earlier");
      else toast.success("Checked in ✓");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="max-w-lg mx-auto px-6 py-16">
        <div className="samaaj-card p-8 text-center">
          <Warning size={40} weight="duotone" className="mx-auto text-[#D95A41]" />
          <div className="font-heading text-2xl mt-2">Ticket not found</div>
          <p className="text-sm text-[#5C564D] mt-2">{err}</p>
        </div>
      </div>
    );
  }
  if (!ticket) {
    return <div className="max-w-lg mx-auto px-6 py-16 text-center text-[#5C564D]">Loading ticket…</div>;
  }

  const status = ticket.checked_in ? "checked_in" : "valid";

  return (
    <div className="max-w-lg mx-auto px-6 py-16">
      <div className="flex items-center justify-center mb-6"><Logo size={40} /></div>
      <div className="samaaj-card p-8" data-testid="verify-card">
        <div className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase inline-flex items-center gap-1 ${status === "checked_in" ? "bg-emerald-100 text-emerald-800" : "bg-[#D49A36]/20 text-[#7a5a10]"}`}>
          {status === "checked_in" ? <><CheckCircle size={12} weight="fill" /> Checked in</> : <><Ticket size={12} /> Valid ticket</>}
        </div>
        <div className="font-heading text-3xl mt-3">{ticket.event_title}</div>
        <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><Clock size={12} /> {new Date(ticket.event_date).toLocaleString()}</div>
        <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><MapPin size={12} /> {ticket.event_venue}</div>

        <div className="mt-5 p-4 rounded-2xl bg-[#F2EBE5] border border-[#E5D9CC]">
          <div className="text-[10px] uppercase tracking-widest text-[#5C564D]">Ticket</div>
          <div className="font-mono font-bold text-2xl text-[#2C4A3B]" data-testid="verify-ticket-number">{ticket.ticket_number}</div>
          <div className="mt-3 text-xs text-[#5C564D] flex items-center gap-1"><User size={12} /> {ticket.attendee_name}</div>
          {ticket.checked_in && (
            <div className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle size={12} weight="fill" />
              Checked in {ticket.checked_in_at ? new Date(ticket.checked_in_at).toLocaleString() : ""}
            </div>
          )}
        </div>

        {canCheckIn && !ticket.checked_in && (
          <button data-testid="btn-checkin" disabled={busy} onClick={doCheckin} className="samaaj-btn-primary w-full mt-5">
            {busy ? "Checking in…" : "Check in this attendee"}
          </button>
        )}
        {!user && (
          <p className="mt-5 text-xs text-[#5C564D] text-center">Log in as the organizer to check this ticket in.</p>
        )}
      </div>
    </div>
  );
}
